import type {ToolPresentation} from "@repo/ui/gailgpt/tool-presentation";
import type {MutationProposal} from "./mutation-proposal";
/**
 * Wingman API Types
 * Based on OpenAPI spec: https://ai.staging-lula.com/openapi/v1.json
 */

import {MessageFeedbackData} from "@/types/feedback";

// =============================================================================
// Core Types
// =============================================================================

/**
 * Roles from the Wingman API spec
 */
export type Role = "assistant" | "system" | "tool" | "user";

/**
 * Conversation types supported by the API
 */
export type ConversationType = "chat" | "gpt" | "sms" | "whats_app";

/**
 * Enum for backward compatibility with existing WingmanRole usage
 * @deprecated Use Role type instead for new code
 */
export enum WingmanRole {
   Assistant = "assistant",
   User = "user",
}

// =============================================================================
// Chart Types
// =============================================================================

export interface PieChartData {
   label: string;
   value: number;
}

export interface PieChart {
   title: string;
   data: PieChartData[];
}

export interface BarChartData {
   label: string;
   value: number;
   group?: string;
}

export interface BarChart {
   title: string;
   data: BarChartData[];
}

/**
 * Alias for backward compatibility - some code uses "Chart" generically
 */
export type Chart = BarChart;

// =============================================================================
// Workspace Building Block Types
// =============================================================================

/**
 * Identifies the workflow or entity a workspace building block belongs to.
 * Shared by every workspace content type so future blocks (campaigns, calls,
 * analytics, ...) reuse the same context shape instead of inventing their own.
 */
export interface WorkspaceContext {
   /** Entity kind, e.g. "new_script", "inbound", "outbound". More kinds will be added over time. */
   type: string;
   /** Stable identifier of the owning entity, when one exists (absent for entities not yet created). */
   id?: string | null;
   /** User-facing name of the owning entity. */
   name?: string | null;
}

/** A single step the agent takes when a use case triggers. */
export interface ScriptDraftAction {
   description: string;
}

/** A situation the script handles, with the ordered steps the agent takes. */
export interface ScriptDraftUseCase {
   title: string;
   description: string;
   actions: ScriptDraftAction[];
}

/** Direction values the script_draft contract allows. */
export type ScriptDraftDirection = "inbound" | "outbound";

/**
 * A complete phone script draft proposed by the assistant. Dynamic values use
 * `{variable}` markup (single curly braces around the variable name).
 */
export interface ScriptDraft {
   context: WorkspaceContext;
   /** Present when revising an existing script; absent for brand-new drafts. */
   scriptId?: string | null;
   scriptName: string;
   /**
    * Required by current Wingman; absent on drafts persisted before the
    * field existed.
    */
   direction?: ScriptDraftDirection | null;
   /** Agent name the user chose in conversation; absent when they didn't. */
   agentName?: string | null;
   summary: string;
   greeting: string;
   goodbye: string;
   voicemail?: string | null;
   useCases: ScriptDraftUseCase[];
}

// =============================================================================
// Document Type
// =============================================================================

export interface Document {
   id: string;
   conversationId: string;
   mediaType: string;
   fileName?: string;
}

// =============================================================================
// Message Content Types (Discriminated Union)
// =============================================================================

export interface MessageContentMetadata {
   type: "metadata";
   id: string;
   role: Role;
   timestamp: string;
}

export interface MessageContentText {
   type: "text";
   content: string;
}

export interface MessageContentBinary {
  type: "binary";
  /** Base64 payload. Empty when the file lives at `url` instead. */
  data: string;
  mimeType: string;
  fileName?: string;
  /** Remote location of the file (e.g. signed storage URL) when it isn't inlined as base64 in `data`. */
  url?: string;
}

/**
 * Persistable file generated server-side and stored in object storage.
 * `downloadUrl` is a signed URL re-issued by the backend on each `GET /messages` (using `objectPath` as the canonical key).
 */
export interface MessageContentGeneratedFile {
   type: "generated_file";
   fileName: string;
   contentType: string;
   objectPath: string;
   downloadUrl: string;
}

export interface MessageContentDocumentUpload {
   type: "document_upload";
   document: Document;
}

export interface MessageContentFunctionCall {
   type: "function_call";
   callId: string;
   functionName: string;
   parameters: Record<string, unknown>;
   presentation?: ToolPresentation;
   presentationUnavailable?: true;
   /** Native SDK approval, preserved in live streams and conversation history. */
   approval?: {
      id: string;
      approved?: boolean;
      reason?: string;
      proposal?: MutationProposal;
      presentation?: ToolPresentation;
      /** The server sent a preview this client could not read; the change cannot be applied from here. */
      previewUnavailable?: true;
   };
}

/** Wingman's `GailApprovalDecision`: exactly one approval ID and the decision, nothing else. */
export interface ToolApprovalResponse {
   id: string;
   approved: boolean;
}

export interface MessageContentFunctionResult {
   type: "function_result";
   callId: string;
   functionName: string;
   result: unknown;
}

export interface MessageContentBarChart {
   type: "bar_chart";
   chart: BarChart;
   callId: string;
   functionName: string;
   parameters: Record<string, unknown>;
}

export interface MessageContentPieChart {
   type: "pie_chart";
   chart: PieChart;
   callId: string;
   functionName: string;
   parameters: Record<string, unknown>;
}

/**
 * First-class workspace content: a phone script draft emitted by the
 * `script_draft` UI-components tool. Rendered as a card in chat and as a
 * structured preview in the workspace (artifact) panel.
 */
export interface MessageContentScriptDraft {
   type: "script_draft";
   callId: string;
   functionName: string;
   parameters: Record<string, unknown>;
   draft: ScriptDraft;
}

/**
 * A request to build a workflow, emitted by legacy Wingman's `build_workflow`
 * UI-components tool.
 */
export interface WorkflowBuild {
   /** Original natural-language workflow request, displayed in conversation history. */
   description: string;
   /** Optional suggested workflow/tool name. */
   suggestedName?: string | null;
}

/**
 * Legacy `build_workflow` request, retained for read-only conversation history.
 */
export interface MessageContentBuildWorkflow {
   type: "build_workflow";
   callId: string;
   functionName: string;
   parameters: Record<string, unknown>;
   workflow: WorkflowBuild;
}

export type ScriptSection = "script" | "knowledge" | "agent" | "timeHours" | "testLaunch";

export interface NavigationTarget {
   direction: ScriptDraftDirection;
   scriptId?: string | null;
   section?: ScriptSection | null;
}

export interface MessageContentNavigation {
   type: "navigation";
   callId: string;
   functionName: string;
   parameters: Record<string, unknown>;
   target: NavigationTarget;
}

export interface MessageContentForm {
   type: "form";
   callId: string;
   functionName: string;
   parameters: Record<string, unknown>;
   formSchema: string;
}

export interface MessageContentFormSubmission {
   type: "form_submission";
   callId: string;
   functionName: string;
   result: unknown;
}

export interface MessageContentNotification {
   type: "notification";
   /** Marks this as a transient SSE event — structurally incompatible with `MessageContent`. */
   readonly _transient: true;
   /** Raw fallback text (used when `key` is not provided or translation is missing). */
   message: string;
   level: "info";
   /** i18n translation key — when present, the client uses `t(key, params)` instead of `message`. */
   key?: string;
   /** Interpolation parameters for the translation key. */
   params?: Record<string, string | number>;
}

export interface MessageContentTokenUsage {
   type: "token_usage";
   /** Marks this as a transient SSE event — structurally incompatible with `MessageContent`. */
   readonly _transient: true;
   usagePercent: number;
   limitReached: boolean;
   breakdown?: {
      conversation: number;
      system: number;
      attachments: number;
   };
}

export interface MessageContentGenerationUsage {
   type: "generation_usage";
   readonly _transient: true;
   inputTokens: number | null;
   outputTokens: number | null;
   totalTokens: number | null;
}

export interface MessageContentRecalledMemories {
   type: "recalled_memories";
   readonly _transient: true;
   facts: string[];
}

export interface MessageSnapshotEvent {
   type: "message_snapshot";
   readonly _transient: true;
   contents: MessageContent[];
}

export interface MessageContentThinking {
   type: "thinking";
   content: string;
}

export interface MessageContentStatus {
   type: "status";
   content: string;
}

/**
 * Terminal status content. Unlike progress statuses (e.g. `analyzing_document`),
 * this is the last event in the stream — no further events follow it. The client
 * must surface it as a persistent message rather than a transient streaming
 * status, which would be cleared on stream close. Mirrors the server's
 * `GenerationTimedOutStatus`.
 */
export const GENERATION_TIMED_OUT_STATUS = "generation_timed_out";

export interface MessageContentCitation {
   type: "citation";
   number: number;
   sourceType: "web" | "document";
   title: string;
   url?: string;
   fileName?: string;
   mediaType?: string;
   pages?: string[];
   callId?: string;
}

/**
 * A durable marker that the assistant could not complete the turn — e.g. a Gemini tool-call text
 * leak that surfaced as a stray `}`. Persisted, so it renders the same on reload as in the live
 * stream. `reason` is a machine code the client localizes; never display it raw.
 */
export interface MessageContentGenerationFailed {
   type: "generation_failed";
   reason: string;
}

/**
 * Persistable message content types — these are stored in conversation history.
 */
export type MessageContent =
   | MessageContentMetadata
   | MessageContentText
   | MessageContentBinary
   | MessageContentGeneratedFile
   | MessageContentDocumentUpload
   | MessageContentFunctionCall
   | MessageContentFunctionResult
   | MessageContentBarChart
   | MessageContentPieChart
   | MessageContentScriptDraft
   | MessageContentBuildWorkflow
   | MessageContentNavigation
   | MessageContentForm
   | MessageContentFormSubmission
   | MessageContentThinking
   | MessageContentStatus
   | MessageContentCitation
   | MessageContentGenerationFailed;

/**
 * Transient SSE-only events — never persisted in conversation history.
 * Separated from `MessageContent` so the type system prevents accidental persistence.
 */
export type StreamingEvent =
   | MessageContentNotification
   | MessageContentTokenUsage
   | MessageContentGenerationUsage
   | MessageContentRecalledMemories
   | MessageSnapshotEvent;

/**
 * Streaming message type (used during SSE streaming).
 * Includes both persistable content and transient events.
 */
export type StreamingConversationMessage = MessageContent | StreamingEvent;

// =============================================================================
// Conversation Message
// =============================================================================

/**
 * Full conversation message structure
 */
export interface ConversationMessage {
   id: string;
   role: Role;
   author: string | null;
   contents: MessageContent[];
   timestamp: Date | string;
   feedback?: MessageFeedbackData;
   /** Client-generated messages (e.g. error/limit notices) that shouldn't expose feedback controls. */
   synthetic?: boolean;
   /** Live-only Mem0 facts for this turn. Stream-only; never persisted or reloaded. */
   recalledFacts?: string[];
}

// =============================================================================
// Request/Response Types
// =============================================================================

/**
 * Request to create a new conversation
 */
export interface CreateConversationRequest {
   assistantId: string;
   type: ConversationType;
}

/**
 * Response from creating a conversation
 */
export interface CreateConversationResponse {
   conversationId: string;
}

/** Mirrors Wingman's `PageEntityContext` record (hand-synced). */
export interface PageEntityContext {
   type: string;
   id?: string;
   name?: string;
   data?: Record<string, unknown>;
}

/** Mirrors Wingman's `PageContext` record (hand-synced). `url` is pathname + query, no origin. */
export interface PageContext {
   url: string;
   title?: string;
   routeName?: string;
   routeDescription?: string;
   entities?: PageEntityContext[];
}

/**
 * The account's product entitlements, sent alongside a chat message.
 *
 * Advisory only: Wingman gates entitlement-scoped tools (e.g. the script_draft workspace
 * builder) authoritatively against Galactus, so this context is a hint/telemetry signal and is
 * never the security boundary — a client cannot unlock a tool by claiming an entitlement here.
 */
export interface EntitlementContext {
   /** The account's product subscriptions (name + subscription status). */
   products: { name?: string; subscriptionStatus: string }[];
   /** Whether the UI considers this a GailGPT-only account (no GailAgent). */
   isGailGptOnly: boolean;
}

/**
 * Request to append a message (triggers AI response with streaming)
 */
export interface AppendConversationMessageRequest {
   contents: MessageContent[];
   /** Opt in only when the caller renders native approval controls. */
   approvalPresentation?: "native" | "text";
   /** One decision for every pending approval in the proposal message; Wingman settles them together. */
   toolApprovalResponses?: ToolApprovalResponse[];
   /** Required with `toolApprovalResponses`. Generate resumes from this proposal instead of appending a user message. */
   expectedProposalMessageId?: string;
   pageContext?: PageContext;
   entitlementContext?: EntitlementContext;
}

/**
 * V2 request to append a message with a specific role (no AI response)
 */
export interface AppendConversationMessageV2Request {
   contents: MessageContent[];
   role: Role;
}

/**
 * Response containing conversation details
 */
export interface ConversationDetailsResponse {
   id: string;
   type: ConversationType;
   assistantId: string;
   userId?: string;
   name?: string;
   createdAt?: string;
   lastModifiedAt?: string;
   isArchived?: boolean;
   phoneNumber?: string;
}

// =============================================================================
// Client Method Parameter Types
// =============================================================================

export interface CreateConversationParams {
   xOrgId: string;
   createConversationRequest: CreateConversationRequest;
}

export interface GetConversationParams {
   xOrgId: string;
   conversationId: string;
}

export interface ListConversationMessagesParams {
   xOrgId: string;
   conversationId: string;
}

export interface AppendConversationMessageParams {
   xOrgId: string;
   conversationId: string;
   appendConversationMessageRequest: AppendConversationMessageRequest;
}

export interface AppendConversationMessageV2Params {
   xOrgId: string;
   conversationId: string;
   appendConversationMessageV2Request: AppendConversationMessageV2Request;
}

/**
 * Additional headers that can be passed to API methods
 */
export interface WingmanRequestOptions {
   headers?: Record<string, string>;
   /** Local display context for approval continuations; never serialized to Wingman. */
   toolApprovalCalls?: readonly MessageContentFunctionCall[];
   /**
    * Caller-owned abort signal. Aborting it cancels the in-flight request; for
    * streaming sends this also stops server-side generation (v2 `/generate`
    * threads the request abort into the model stream and discards the partial
    * assistant message instead of committing it).
    */
   signal?: AbortSignal;
}
